import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import { CircularProgress, IconButton } from "@mui/material";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import { useEffect, useState } from "react";
import { searchSpeciesByName } from "@services/externalTaxonomicGroup/search/index";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import {
  taxonomicGroupDefaultValues,
  taxonomicGroupsSchema,
  type TaxonomicGroupsFormData,
} from "../schemas/taxonomicGroupSchema";
import { useGetTaxonomicGroupById } from "@services/taxonomicGroups/getById";
import { updateTaxonomicGroup } from "@services/taxonomicGroups/update";
import { globalMutate } from "@utils/swr";

function ModalEditTaxonomicGroups() {
  const { isModalOpen, closeModal, setIsLoading, dataModal } = usePage();
  const open = isModalOpen("editTaxonomicGroup");
  const [isFetching, setIsFetching] = useState(false);

  const id = dataModal?.id as string;
  const { data, isLoading, error } = useGetTaxonomicGroupById(id);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm<TaxonomicGroupsFormData>({
    resolver: zodResolver(taxonomicGroupsSchema),
    defaultValues: taxonomicGroupDefaultValues,
  });

  useEffect(() => {
    if (open && data) {
      setValue("order", data.order);
      setValue("class", data.class);
      setValue("family", data.family);
      setValue("phylum", data.phylum);
      setValue("rein", data.rein);
      setValue("genus", data.genus);
      setValue("species", data.species);
    }
  }, [open, data, setValue]);

  const onSubmit = async (data: TaxonomicGroupsFormData) => {
    try {
      setIsLoading(true);

      const taxonomicGroupId = id;

      await updateTaxonomicGroup(taxonomicGroupId, {
        rein: data.rein,
        phylum: data.phylum,
        class: data.class,
        order: data.order,
        family: data.family,
        genus: data.genus,
        species: data.species,
      });

      await globalMutate([
        "fetch-get-taxonomic-groups",
        `fetch-taxonomic-group-${taxonomicGroupId}`,
      ]);
      toast.success("Grupo taxonômico atualizado com sucesso");

      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError
        ? error.message
        : "Erro ao atualizar grupo taxonômico";

      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    closeModal();
    reset();
  };

  const handleSearchSpecies = async () => {
    const speciesName = getValues("species");

    if (!speciesName) {
      toast.warn("Por favor, insira um nome de espécie para buscar.");
      return;
    }

    try {
      setIsFetching(true);
      const taxonomyData = await searchSpeciesByName(speciesName);

      if (!taxonomyData) {
        throw new Error("Erro na resposta da API do GBIF.");
      }

      if (taxonomyData.matchType === "NONE") {
        toast.error(`Espécie "${speciesName}" não encontrada.`);
        return;
      }

      setValue("rein", taxonomyData.kingdom || "", { shouldValidate: true });
      setValue("phylum", taxonomyData.phylum || "", { shouldValidate: true });
      setValue("class", taxonomyData.class || "", { shouldValidate: true });
      setValue("order", taxonomyData.order || "", { shouldValidate: true });
      setValue("family", taxonomyData.family || "", { shouldValidate: true });
      setValue("genus", taxonomyData.genus || "", { shouldValidate: true });
      setValue("species", taxonomyData.species || "", { shouldValidate: true });

      toast.success("Dados taxonômicos preenchidos!");
    } catch {
      toast.error(
        "Funcionalidade indisponível. Por favor, preencha manualmente.",
      );
    } finally {
      setIsFetching(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      heading="Atualizar um Grupo Taxonômico"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-2xl relative"
      isLoading={isLoading}
      error={!!error}
    >
      {isFetching && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10 rounded-lg">
          <CircularProgress color="secondary" />
        </div>
      )}
      <form className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <Controller
          name="rein"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Reino"
              textSize="sm"
              fullWidth
              error={!!errors.rein}
              helperText={errors.rein?.message}
            />
          )}
        />

        <Controller
          name="phylum"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Filo"
              textSize="sm"
              fullWidth
              error={!!errors.phylum}
              helperText={errors.phylum?.message}
            />
          )}
        />

        <Controller
          name="class"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Classe"
              textSize="sm"
              fullWidth
              error={!!errors.class}
              helperText={errors.class?.message}
            />
          )}
        />

        <Controller
          name="order"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Ordem"
              textSize="sm"
              fullWidth
              error={!!errors.order}
              helperText={errors.order?.message}
            />
          )}
        />

        <Controller
          name="family"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Família"
              textSize="sm"
              fullWidth
              error={!!errors.family}
              helperText={errors.family?.message}
            />
          )}
        />

        <Controller
          name="genus"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Gênero"
              textSize="sm"
              fullWidth
              error={!!errors.genus}
              helperText={errors.genus?.message}
            />
          )}
        />

        <div className="col-span-1 md:col-span-2 flex items-end gap-2">
          <Controller
            name="species"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Espécie"
                textSize="sm"
                fullWidth
                error={!!errors.species}
                helperText={errors.species?.message}
              />
            )}
          />
          <IconButton onClick={handleSearchSpecies} disabled={isFetching}>
            <ContentPasteSearchIcon />
          </IconButton>
        </div>
      </form>
    </Modal>
  );
}

export default ModalEditTaxonomicGroups;
